
import { useAction } from "convex/react";
import { api } from "../../api";
import { useMemo, useEffect, useState } from "react";

export interface CommandHistoryItem {
  id: string;
  name: string;
  status: "completed" | "failed" | "pending";
  timestamp: number;
  error?: string;
  date_time_issued_epoch?: number;
}

export const useDeviceCommandHistory = (deviceId: string) => {
  const getHistory = useAction(api.devices.get.history);
  const [historyQuery, setHistoryQuery] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsLoading(true);
        const result = await getHistory({ device: deviceId as any });
        console.log("📊 Command History API Response:", result);
        console.log("📊 Response Structure:", {
          hasCompleted: !!result?.completed,
          hasF: !!result?.failed,
          hasPending: !!result?.pending,
          completedCount: result?.completed?.length || 0,
          failedCount: result?.failed?.length || 0,
          pendingCount: result?.pending?.length || 0,
        });
        if (result?.completed?.[0]) {
          console.log("📊 Sample Completed Command:", result.completed[0]);
        }
        if (result?.failed?.[0]) {
          console.log("📊 Sample Failed Command:", result.failed[0]);
        }
        if (result?.pending?.[0]) {
          console.log("📊 Sample Pending Command:", result.pending[0]);
        }
        setHistoryQuery(result);
      } catch (error) {
        console.error("Failed to fetch command history:", error);
        setHistoryQuery(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (deviceId) {
      fetchHistory();
    }
  }, [deviceId]);

  const formattedHistory = useMemo((): CommandHistoryItem[] => {
    if (!historyQuery || !historyQuery.completed || !historyQuery.failed || !historyQuery.pending) {
      return [];
    }

    const history: CommandHistoryItem[] = [];

    // Add completed commands
    historyQuery.completed.forEach((cmd: any) => {
      history.push({
        id: `completed-${cmd.name}-${cmd.date_time_completed_epoch}`,
        name: cmd.name,
        status: "completed",
        timestamp: cmd.date_time_completed_epoch,
      });
    });

    // Add failed commands
    historyQuery.failed.forEach((cmd: any) => {
      history.push({
        id: `failed-${cmd.name}-${cmd.date_time_failed_epoch}`,
        name: cmd.name,
        status: "failed",
        timestamp: cmd.date_time_failed_epoch,
        error: cmd.error,
        date_time_issued_epoch: cmd.date_time_issued_epoch,
      });
    });

    // Add pending commands
    historyQuery.pending.forEach((cmd: any) => {
      history.push({
        id: `pending-${cmd.name}-${cmd.date_time_issued_epoch}`,
        name: cmd.name,
        status: "pending",
        timestamp: cmd.date_time_issued_epoch,
        date_time_issued_epoch: cmd.date_time_issued_epoch,
      });
    });

    // Sort by timestamp (most recent first)
    return history.sort((a, b) => b.timestamp - a.timestamp);
  }, [historyQuery]);

  const refetch = async () => {
    try {
      setIsLoading(true);
      const result = await getHistory({ device: deviceId as any });
      console.log("🔄 Refetch Command History API Response:", result);
      setHistoryQuery(result);
    } catch (error) {
      console.error("Failed to refetch command history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    history: formattedHistory,
    isLoading,
    refetch,
  };
};
